<template>
  <ConversationHeader
    :date="dateSent"
    :author-name="getAuthorName"
    :author-avatar="authorAvatar"
    :author-id="authorId"
    :moderation-enabled="moderationEnabled"
    :is-moderator="isModerator"
    @redirect="handleRedirect"
  />
  <MessageFlags
    v-if="isAuthor"
    :status="status"
    :moderation-status="moderationStatus"
    :timestamp-updated="dateUpdated"
  />
  <iRow v-if="isEditing || isEditSaving">
    <iColumn>
      <iEditor
        v-model="commentEdit"
        name="editComment"
        @enter="saveEdit"
      />
      <iRow vertical-align="middle">
        <iColumn width="hug">
          <iButton variant="tertiary" @click="cancelEditing">
            Cancel
          </iButton>
        </iColumn>
        <iColumn width="hug">
          <iButton :is-loading="isEditSaving" loading-spinner-position="inside" @click="saveEdit">
            Save
          </iButton>
        </iColumn>
      </iRow>
    </iColumn>
  </iRow>
  <template v-else>
    <iRow>
      <iText>
        {{ message }}
      </iText>
    </iRow>
    <iText v-if="showAuthorPendingMessage" variant="subtle">
      Not visible, pending moderation.
    </iText>
    <iText v-if="showAuthorBlockedMessage" variant="subtle">
      This message has been blocked.
    </iText>
    <iText v-if="isMessageLoading[messageId]?.error" variant="subtle">
      {{ isMessageLoading[messageId].error }}
    </iText>
    <iRow v-if="isModerator && moderationEnabled" align="between">
      <iText variant="accent">
        Report Count: {{ numReports }}
      </iText>
      <iText variant="accent">
        moderation score: {{ moderationScore }}
      </iText>
    </iRow>
    <iColumn class="conversation-footer-container">
      <ConversationFooter
        :current-message="message"
        :author-id="authorId"
        :message-id="messageId"
        :vote-score="voteScore"
        :num-replies="numApprovedReplies"
        :num-likes="numLikes"
        :moderation-status="moderationStatus"
        :status="status"
        :user-liked="userLiked"
        :user-up-voted="userUpVoted"
        :user-down-voted="userDownVoted"
        :reactions="reactions"
        :can-reply-to-comments="canReplyToComments"
        :can-like-comments="canLikeComments"
        :can-vote-on-comments="canVoteOnComments"
        @start-editing="turnOnIsEditing"
        @report="report"
        @reply="toggleIsReplying"
        @approve="approve"
        @escalate="escalate"
        @block="block"
        @delete="deleteComment"
        @undelete="unDeleteComment"
        @like="like"
        @unlike="unlike"
        @upvote="upVote"
        @revokeupvote="revokeUpVote"
        @downvote="downVote"
        @revokedownvote="revokeDownVote"
      />
    </iColumn>
    <iRow
      v-if="isReplying"
      :style-overrides="{
        marginLeft: 'extraLarge',
      }"
    >
      <iSpace
        :height="1"
        width="fill"
        :style-overrides="{
          backgroundColor: 'standard',
        }"
      />
      <iRow
        v-if="replies.length > 0"
        class="replies-container"
      >
        <ActiveCommentWidget
          v-for="replyMessage in replies"
          :key="replyMessage.messageId"
          :message-id="replyMessage.messageId"
          :replies="replyMessage.replies ? replyMessage.replies : []"
          :reply-id="replyMessage.replyId"
          :conversation-id="replyMessage.conversationId"
          :author-id="replyMessage.userId"
          :author-avatar="replyMessage.avatarUrl"
          :contains-unread-mention-for-viewer="messageContainsUnreadMentionForViewer(replyMessage.messageId)"
          :message="replyMessage.message"
          :vote-score="replyMessage.voteScore"
          :date-sent="replyMessage.timestampCreated"
          :author-name="replyMessage.authorName"
          :moderation-status="replyMessage.moderationStatus"
          :num-approved-replies="replyMessage.numApprovedReplies"
          :num-likes="replyMessage.numLikes"
          :num-reports="replyMessage.numReports"
          :moderation-score="replyMessage.moderationScore"
          :status="replyMessage.status"
          :date-updated="replyMessage.timestampUpdated"
          :is-deleted="false"
          :user-liked="replyMessage.userLiked"
          :user-up-voted="replyMessage.userUpVoted"
          :user-down-voted="replyMessage.userDownVoted"
          :can-vote-on-comments="canLikeComments"
          :can-like-comments="canLikeComments"
          :can-reply-to-comments="canReplyToComments"
        />
      </iRow>
      <NewReply
        context="reply"
        :entity-id="messageId"
        :conversation-id="conversationId"
      />
    </iRow>
  </template>
</template>

<script>

import { mapState, mapActions } from "pinia";
import { useConversationStore } from "%/stores/conversation-store";
import { useGeneralStore } from "%/stores/general-store";
import ConversationFooter from "%/components/community/chatrooms/comments/ConversationFooter";
import ConversationHeader from "%/components/community/chatrooms/comments/ConversationHeader";
import MessageFlags from "%/components/community/chatrooms/comments/MessageFlags";
import { DEFAULT_USER_AVATAR } from "%/constants/user-constants";
import NewReply from "%/components/community/chatrooms/comments/NewReply.vue";

export default {
  name: "ActiveCommentWidget",
  components: {
    NewReply,
    ConversationHeader,
    ConversationFooter,
    MessageFlags,
  },
  props: {
    message: {
      type: String,
      required: true,
    },
    messageId: {
      type: Number,
      required: true,
    },
    conversationId: {
      type: Number,
      required: true,
    },
    dateSent: {
      type: Number,
      required: false,
      default: Date.now(),
    },
    numApprovedReplies: {
      type: Number,
      required: false,
      default: 0,
    },
    numLikes: {
      type: Number,
      required: false,
      default: 0,
    },
    voteScore: {
      type: Number,
      required: false,
      default: 0,
    },
    authorName: {
      type: String,
      required: true,
    },
    authorAvatar: {
      type: String,
      required: false,
      default: DEFAULT_USER_AVATAR,
    },
    authorId: {
      type: Number,
      required: true,
    },
    allowsEditing: {
      type: Boolean,
      required: false,
      default: true,
    },
    moderationStatus: {
      type: String,
      required: true,
    },
    status: {
      type: String,
      required: true,
    },
    numReports: {
      type: Number,
      required: true,
    },
    moderationScore: {
      type: Number,
      required: true,
    },
    dateUpdated: {
      type: Number,
      required: true,
    },
    isDeleted: {
      type: Boolean,
      required: true,
    },
    replies: {
      type: Array,
      required: true,
    },
    userLiked: {
      type: Boolean,
      required: true,
    },
    userUpVoted: {
      type: Boolean,
      required: true,
    },
    userDownVoted: {
      type: Boolean,
      required: true,
    },
    canLikeComments: {
      type: Boolean,
      required: false,
      default: false,
    },
    canReplyToComments: {
      type: Boolean,
      required: false,
      default: false,
    },
    canVoteOnComments: {
      type: Boolean,
      required: false,
      default: false,
    },
    reactions: {
      type: Array,
      required: false,
      default: [],
    },
  },
  styleGuide: () => ({
    borderColor: { "color.background": "light" },
  }),
  data() {
    return {
      isEditing: false,
      isReplying: false,
      commentEdit: "",
      newCommentReply: "",
      currentOffense: null,
      showMenuOptions: false,
      showReviewOptions: false,
      showReportOptions: false,
      likedComment: false,
      isSharing: false,
      bookmarkedComment: false,
    };
  },
  computed: {
    ...mapState(useGeneralStore, ["userData", "userAvatar"]),
    ...mapState(useConversationStore, ["isModerator", "moderationEnabled", "isMessageLoading"]),
    isEditSaving() {
      if (!this.isMessageLoading[this.messageId]) {
        return false;
      }
      if (this.isMessageLoading[this.messageId].edit) {
        return true;
      }
      return false;
    },
    isAuthor() {
      return this.userData.user_id === this.authorId;
    },
    getAuthorName() {
      if (this.isAuthor) {
        return this.userData.first_name;
      }
      return this.authorName;
    },
    showAuthorPendingMessage() {
      return this.isAuthor && this.moderationStatus === "unknown";
    },
    showAuthorBlockedMessage() {
      return this.isAuthor && this.moderationStatus == "blocked";
    },
  },
  methods: {
    ...mapActions(useConversationStore, {
      fetchMore: "fetchMore",
      editMessage: "editMessage",
      likeMessage: "likeMessage",
      unLikeMessage: "unLikeMessage",
      deleteMessage: "deleteMessage",
      undeleteMessage: "undeleteMessage",
      reportMessage: "reportMessage",
      upVoteMessage: "upVoteMessage",
      revokeUpVoteMessage: "revokeUpVoteMessage",
      downVoteMessage: "downVoteMessage",
      revokeDownVoteMessage: "revokeDownVoteMessage",
      fetchComments: "fetchComments",
      replyToMessage: "replyToMessage",
      approveMessage: "approveMessage",
      blockMessage: "blockMessage",
      escalateMessage: "escalateMessage",
      messageContainsUnreadMentionForViewer: "messageContainsUnreadMentionForViewer",
    }),
    turnOnIsEditing() {
      this.commentEdit = this.message;
      this.isEditing = true;
    },
    toggleIsReplying() {
      this.isReplying = !this.isReplying;
    },
    saveEdit() {
      if (this.commentEdit === "") {
        return;
      }
      this.isEditing = false;
      this.editMessage(this.messageId, this.commentEdit);
    },
    cancelEditing() {
      this.isEditing = false;
    },
    deleteComment() {
      this.showMenuOptions = false;
      this.deleteMessage(this.messageId);
    },
    unDeleteComment() {
      this.showMenuOptions = false;
      this.undeleteMessage(this.messageId);
    },
    report(reason) {
      this.showReportOptions = false;
      this.reportMessage(this.messageId, reason);
    },
    reply(newMessage) {
      this.replyToMessage(this.messageId, newMessage);
    },
    like() {
      this.likeMessage(this.messageId);
    },
    unlike() {
      this.unLikeMessage(this.messageId);
    },
    upVote() {
      this.upVoteMessage(this.messageId);
    },
    revokeUpVote() {
      this.revokeUpVoteMessage(this.messageId);
    },
    downVote() {
      this.downVoteMessage(this.messageId);
    },
    revokeDownVote() {
      this.revokeDownVoteMessage(this.messageId);
    },
    block(reason) {
      this.blockMessage(this.messageId, reason);
    },
    approve() {
      this.approveMessage(this.messageId);
    },
    escalate() {
      this.escalateMessage(this.messageId);
    },
    handleRedirect() {
      if (this.editMode) {
        return;
      }
      window.location.href = useGeneralStore().fanUrl + `/comment/${this.messageId}`;
    },
  },
};
</script>

<style scoped lang="scss">
</style>
